import { autoTranslate } from './autoTranslate';

// Reversible fallback for legacy UI text. Never changes field values or saved data.
export function createDomTranslationSession(root, language, translate = autoTranslate) {
  const texts = new Map();
  const attributes = new Map();
  const excluded = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'NOSCRIPT', 'TEXTAREA', 'INPUT']);
  const optedOut = element => {
    for (let el = element; el; el = el.parentElement) {
      if (el.hasAttribute('data-i18n-skip') || el.isContentEditable || (el.hasAttribute('contenteditable') && el.getAttribute('contenteditable') !== 'false')) return true;
    }
    return false;
  };
  const skippedText = node => {
    for (let el = node.parentElement; el; el = el.parentElement) {
      if (excluded.has(el.tagName)) return true;
    }
    return optedOut(node.parentElement);
  };
  const scan = () => {
    for (const node of texts.keys()) if (!node.isConnected) texts.delete(node);
    for (const el of attributes.keys()) if (!el.isConnected) attributes.delete(el);
    const walker = root.ownerDocument.createTreeWalker(root, 4);
    let node;
    while ((node = walker.nextNode())) {
      if (skippedText(node)) continue;
      const current = node.nodeValue;
      const previous = texts.get(node);
      const original = previous && current === previous.last ? previous.original : current;
      if (!original?.trim() || original.trim().length > 200) continue;
      const next = translate(original, language);
      if (typeof next !== 'string' || next === original) continue;
      texts.set(node, { original, last: next });
      if (current !== next) node.nodeValue = next;
    }
    root.querySelectorAll('[placeholder], [title], [aria-label]').forEach(el => {
      if (optedOut(el)) return;
      for (const attr of ['placeholder', 'title', 'aria-label']) {
        const current = el.getAttribute(attr);
        if (current == null) continue;
        const previous = attributes.get(el)?.get(attr);
        const original = previous && current === previous.last ? previous.original : current;
        if (!original.trim() || original.length > 200) continue;
        const next = translate(original, language);
        if (typeof next !== 'string' || next === original) continue;
        if (!attributes.has(el)) attributes.set(el, new Map());
        attributes.get(el).set(attr, { original, last: next });
        if (current !== next) el.setAttribute(attr, next);
      }
    });
  };
  const restore = () => {
    for (const [node, record] of texts) {
      if (node.isConnected && node.nodeValue === record.last) node.nodeValue = record.original;
    }
    for (const [el, records] of attributes) {
      if (!el.isConnected) continue;
      for (const [attr, record] of records) if (el.getAttribute(attr) === record.last) el.setAttribute(attr, record.original);
    }
    texts.clear(); attributes.clear();
  };
  return { scan, restore };
}
